import styled, { keyframes, css } from "styled-components";

const floatUp = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, 0) scale(0.8);
  }
  20% {
    opacity: 1;
    transform: translate(-50%, -10px) scale(1.1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -60px) scale(0.9);
  }
`;

export const FloatingText = styled.div`
  position: absolute;
  pointer-events: none;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 800;
  letter-spacing: 1px;
  white-space: nowrap;
  z-index: 5;
  animation: ${floatUp} 0.9s ease-out forwards;
  text-shadow: 0 0 12px currentColor;

  ${(p) =>
    p.$variant === "score" &&
    css`
      color: var(--accent);
      font-size: 22px;
    `}
  ${(p) =>
    p.$variant === "bonus" &&
    css`
      color: var(--warn);
      font-size: 13px;
      letter-spacing: 2px;
    `}
  ${(p) =>
    p.$variant === "powerup" &&
    css`
      color: #a78bfa;
      font-size: 16px;
      letter-spacing: 3px;
    `}
`;

const bannerIn = keyframes`
  0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.7); }
  20%  { opacity: 1; transform: translate(-50%, -50%) scale(1.05); }
  80%  { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.2); }
`;

const particleFly = keyframes`
  0%   { opacity: 1; transform: translate(-50%, -50%) translate(0, 0) rotate(0deg) scale(1); }
  100% { opacity: 0; transform: translate(-50%, -50%) translate(var(--dx), var(--dy)) rotate(var(--rot)) scale(0.3); }
`;

export const ParticleBurst = styled.div`
  position: absolute;
  pointer-events: none;
  z-index: 4;
`;

export const ParticlePiece = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  left: 0;
  top: 0;
  animation: ${particleFly} 0.65s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  box-shadow: 0 0 8px currentColor;
`;

const overlayIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slowPulse = keyframes`
  0%, 100% { box-shadow: inset 0 0 80px rgba(96, 165, 250, 0.45); }
  50%      { box-shadow: inset 0 0 140px rgba(96, 165, 250, 0.7); }
`;

export const SlowOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 7;
  background: radial-gradient(ellipse at center, transparent 35%, rgba(96, 165, 250, 0.18) 100%);
  animation: ${overlayIn} 0.2s ease-out, ${slowPulse} 1.6s ease-in-out infinite;
  border: 2px solid rgba(96, 165, 250, 0.4);
`;

const freezeBreathe = keyframes`
  0%, 100% { box-shadow: inset 0 0 100px rgba(103, 232, 249, 0.18); }
  50%      { box-shadow: inset 0 0 140px rgba(103, 232, 249, 0.28); }
`;

export const FreezeOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 7;
  background: radial-gradient(ellipse at center, transparent 45%, rgba(103, 232, 249, 0.1) 100%);
  animation: ${overlayIn} 0.3s ease-out, ${freezeBreathe} 2.4s ease-in-out infinite;
  border: 1px solid rgba(103, 232, 249, 0.25);
`;

const fall = keyframes`
  0%   { transform: translate(0, -10px); opacity: 0; }
  15%  { opacity: 0.6; }
  85%  { opacity: 0.5; }
  100% { transform: translate(var(--drift), 60px); opacity: 0; }
`;

export const Snowflake = styled.div`
  position: absolute;
  width: var(--size);
  height: var(--size);
  background: rgba(207, 250, 254, 0.7);
  border-radius: 50%;
  filter: blur(1px);
  animation: ${fall} 4s linear infinite;
  animation-delay: var(--delay);
  box-shadow: 0 0 4px rgba(207, 250, 254, 0.6);
`;

export const FrostCorner = styled.div`
  position: absolute;
  width: 320px;
  height: 320px;
  pointer-events: none;
  opacity: 0.5;
  background: radial-gradient(circle at ${(p) =>
    p.$pos === "tl" ? "0% 0%" :
    p.$pos === "tr" ? "100% 0%" :
    p.$pos === "bl" ? "0% 100%" : "100% 100%"
  }, rgba(207, 250, 254, 0.25), rgba(103, 232, 249, 0.05) 50%, transparent 75%);
  ${(p) => p.$pos === "tl" && css`top: 0; left: 0;`}
  ${(p) => p.$pos === "tr" && css`top: 0; right: 0;`}
  ${(p) => p.$pos === "bl" && css`bottom: 0; left: 0;`}
  ${(p) => p.$pos === "br" && css`bottom: 0; right: 0;`}
`;

const labelPulse = keyframes`
  0%, 100% { transform: translateX(-50%) scale(1); opacity: 0.9; }
  50%      { transform: translateX(-50%) scale(1.06); opacity: 1; }
`;

export const EffectLabel = styled.div`
  position: absolute;
  top: 14%;
  left: 50%;
  transform: translateX(-50%);
  font-family: 'JetBrains Mono', monospace;
  font-size: 32px;
  font-weight: 800;
  letter-spacing: 12px;
  text-transform: uppercase;
  padding: 12px 28px;
  border-radius: 12px;
  animation: ${labelPulse} 1.2s ease-in-out infinite;

  ${(p) => p.$variant === "slow" && css`
    color: #93c5fd;
    background: rgba(8, 18, 40, 0.6);
    border: 1px solid rgba(147, 197, 253, 0.6);
    text-shadow: 0 0 18px rgba(96, 165, 250, 0.8);
    box-shadow: 0 0 30px rgba(96, 165, 250, 0.4);
  `}
  ${(p) => p.$variant === "freeze" && css`
    color: #cffafe;
    background: rgba(8, 30, 36, 0.6);
    border: 1px solid rgba(207, 250, 254, 0.7);
    text-shadow: 0 0 18px rgba(103, 232, 249, 0.9);
    box-shadow: 0 0 30px rgba(103, 232, 249, 0.5);
  `}
`;

const shockwaveExpand = keyframes`
  0%   { width: 50px; height: 50px; opacity: 1; border-width: 12px; }
  100% { width: 180vmax; height: 180vmax; opacity: 0; border-width: 1px; }
`;

const bombFlashAnim = keyframes`
  0%   { opacity: 0; }
  10%  { opacity: 1; }
  100% { opacity: 0; }
`;

export const PauseOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  background: rgba(6, 7, 10, 0.78);
  backdrop-filter: blur(8px);
  animation: ${overlayIn} 0.2s ease-out;
`;

export const PauseTitle = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 64px;
  font-weight: 800;
  letter-spacing: 12px;
  color: var(--accent);
  text-shadow: 0 0 30px var(--accent-glow);
`;

export const PauseHint = styled.div`
  color: var(--text-dim);
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  letter-spacing: 3px;
  text-transform: uppercase;

  kbd {
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 2px 8px;
    margin: 0 4px;
    color: var(--accent);
    font-family: inherit;
  }
`;

export const BombOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Shockwave = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 12px solid #fbbf24;
  box-shadow:
    0 0 60px rgba(251, 191, 36, 0.85),
    inset 0 0 40px rgba(251, 191, 36, 0.6);
  animation: ${shockwaveExpand} 0.65s cubic-bezier(0.22, 1, 0.36, 1) forwards;
`;

export const BombFlash = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, rgba(255, 220, 130, 0.55) 0%, rgba(251, 146, 60, 0.3) 30%, transparent 65%);
  animation: ${bombFlashAnim} 0.6s ease-out forwards;
`;

export const WaveBanner = styled.div`
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 6;
  animation: ${bannerIn} 1.8s ease-in-out forwards;

  .label {
    color: var(--text-dim);
    font-family: 'JetBrains Mono', monospace;
    font-size: 14px;
    letter-spacing: 6px;
    text-transform: uppercase;
    margin-bottom: 12px;
  }

  .text {
    color: var(--accent);
    font-family: 'JetBrains Mono', monospace;
    font-size: 72px;
    font-weight: 800;
    letter-spacing: 8px;
    text-transform: uppercase;
    text-shadow:
      0 0 30px var(--accent-glow),
      0 0 60px var(--accent-glow);
  }
`;
